import React, { useEffect, useState } from 'react';
import { Produit, getProduits, updateProduit, addProduit } from '../../services/apiProduits';
import { ButtonVariant } from '../../utils/utils';
import Section from '../../components/base/Section';
import { Title } from '../../components/base/Title';
import { DataTable } from '../../components/base/DataTable';
import { parseToCurrency } from '../../context/panier-context';
import { Dialog } from "../../components/base/Dialog";
import { Input } from "../../components/base/Input";
import { Button } from "../../components/base/Button";
import { handleApiError } from 'utils/errorHandlers';
import { ApiError } from 'components/ApiError';
import { ApiLoading } from 'components/ApiLoading';

export const GestionStocks: React.FC = () => {
	const [produits, setProduits] = useState<Produit[]>([]);
	const [produitToEdit, setProduitToEdit] = useState<Produit | null>(null);
	type ActionMode = 'CREATE' | 'UPDATE' | 'STOCK';
	const emptyForm = {
		libelle: '',
		description: '',
		prix: '',
		stockDisponible: '',
	}
	const [form, setForm] = useState(emptyForm);
	const [loading, setLoading] = useState<{state: boolean, type: 'general' | 'form'}>({state: true, type: 'general'});

	const [open, setOpen] = useState<boolean>(false);
	const [actionMode, setActionMode] = useState<ActionMode>('CREATE');
	const [errors, setErrors] = useState<Record<string, string[]>>({});

	useEffect(() => {
		const fetchProduits = async () => {
			try {
				const data = await getProduits();
				setProduits(data);
			} catch (error: any) {
				setErrors(handleApiError(error));
			} finally {
				setLoading({state: false, type: 'general'});
			}
		};

		fetchProduits();
	}, []);

	useEffect(() => {
		if (produitToEdit) {
			setForm({
				libelle: produitToEdit.libelle || '',
				description: produitToEdit.description || '',
				prix: produitToEdit.prix || '',
				stockDisponible: produitToEdit.stockDisponible?.toString() || '',
			});
		}
	}, [produitToEdit]);

	const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
		setForm({ ...form, [e.target.name]: e.target.value });
	};

	const handleSubmit = async (e: React.FormEvent) => {
		setLoading({state: true, type: 'form'});

		e.preventDefault();
		if (actionMode === 'CREATE') {
			try {
				const add = await addProduit({
					libelle: form.libelle,
					description: form.description,
					prix: Number(form.prix),
					quantite: Number(form.stockDisponible),
				});

				setProduits([...produits, add]);
				handleClose()
			} catch (error: any) {
				setErrors(handleApiError(error, 'form'));
			} finally {
				setLoading({state: false, type: 'form'});
			}

		} else {
			if (!produitToEdit) return;

			try {
				const updated = await updateProduit(produitToEdit.id, {
					libelle: form.libelle,
					description: form.description,
					prix: Number(form.prix),
					quantite: Number(form.stockDisponible),
				});

				setProduits((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));
				handleClose()
			} catch (error: any) {
				setErrors(handleApiError(error, 'form'));
			} finally {
				setLoading({state: false, type: 'form'});
			}
		}
	};

	const handleCreate = () => {
		setActionMode('CREATE');
		setForm(emptyForm);
		setOpen(true);
	}
	const handleEdit = (produit: Produit) => {
		setActionMode('UPDATE');
		setProduitToEdit(produit);
		setOpen(true);
	}
	const handleStock = (produit: Produit) => {
		setActionMode('STOCK');
		setProduitToEdit(produit);
		setOpen(true);
	}
	const handleClose = () => {
		setProduitToEdit(null);
		setErrors({});
		setOpen(false);
	}

  if (errors && errors.general) {
    return <ApiLoading type="error">{errors.general[0]}</ApiLoading>;
  }

  if (loading.type === 'general' && loading.state) {
    return <ApiLoading type="loading">Chargement des produits...</ApiLoading>;
  }

  if (produits.length === 0) {
    return <ApiLoading type="no-data">Aucun produit trouvé</ApiLoading>;
  }

	return (
		<Section fullwidth>
			<Dialog
				open={open}
				onClose={() => handleClose()}
				title={actionMode === 'CREATE' ? "Ajout d'un produit" :
					actionMode === 'UPDATE' ? "Modification du produit" : "Modifier le stock"}
				customBtn={true}
			>
				<form onSubmit={handleSubmit}>
					{actionMode !== "STOCK" &&
						<>
							<Input
								label="Nom"
								name="libelle"
								type="text"
								value={form.libelle}
								onChange={handleChange}
								className="mb-3"
								required
								width="100%"
							/>
							<Input
								label="Description"
								name="description"
								type="text"
								value={form.description}
								onChange={handleChange}
								className="mb-3"
								required
								width="100%"
							/>
							<Input
								label="Prix"
								name="prix"
								type="number"
								step="0.1"
								value={form.prix}
								onChange={handleChange}
								className="mb-3"
								required
								width="100%"
							/>
						</>
					}
					<Input
						label="Stock"
						name="stockDisponible"
						type="number"
						value={form.stockDisponible}
						onChange={handleChange}
						className="mb-3"
						required
						width="100%"
					/>

					{errors.form && (
						<ApiError>
							{errors.form[0]}
						</ApiError>
					)}


					<div className='flex justify-end gap-2 mt-6'>
						<Button type="button" onClick={() => handleClose()} variant='text'>Annuler</Button>
						<Button type="submit" variant="primary">{loading.type === 'form' && loading.state ? 'Veuillez patienter...' : 'Confirmer'}</Button>
					</div>
				</form>
			</Dialog>

			<div className="flex justify-between items-center gap-2 mb-6">
				<Title type="h2">Gestion des stocks</Title>
				<Button variant='primary' onClick={() => handleCreate()}>Ajouter</Button>
			</div>
			<DataTable
				columns={[
					{
						key: 'image_url', label: 'Image',
						render: (value, row) => <img className='h-10 w-10 rounded-full object-cover' src={value}
							alt={`image de ${row.libelle}`} />
					},
					{ key: 'libelle', label: 'Nom' },
					{ key: 'description', label: 'Description' },
					{ key: 'prix', label: 'Prix', render: (value) => parseToCurrency(value) },
					{ key: 'stockDisponible', label: 'Stock' },
				]}
				data={produits}
				actions={[
					{
						label: 'Changer le stock',
						buttonVariant: 'primary' as ButtonVariant,
						onClick: (row: Produit) => handleStock(row)
					},
					{
						label: 'Editer',
						buttonVariant: 'primary-outline' as ButtonVariant,
						onClick: (row: Produit) => handleEdit(row)
					}
				]}
				actionWidth='w-[270px]'
			/>
		</Section>
	);
}
	;